import { useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { useEmployees } from '@/shared/hooks/useEmployees';
import { cn } from '@/shared/utils/utils';
import type { Employee } from '@momentum/types';
import { QuestionShell } from '../QuestionShell';
import { describeStrings } from '../../utils/describe.strings';
import type { PeopleAnswer } from '../../types/describe.types';

interface PeopleQuestionProps {
  status: 'active' | 'answered' | 'upcoming';
  isLast: boolean;
  answer: PeopleAnswer | null;
  onSubmit: (answer: PeopleAnswer) => void;
}

export function PeopleQuestion({
  status,
  isLast,
  answer,
  onSubmit,
}: PeopleQuestionProps) {
  if (status === 'answered' && answer) {
    return (
      <QuestionShell
        kind="core"
        stepNumber={4}
        heading={`4. ${describeStrings.people.heading}`}
        status="answered"
        isLast={isLast}
      >
        <div className="flex flex-wrap gap-1.5">
          {answer.employees.length === 0 ? (
            <span className="text-sm text-slate-400">
              {describeStrings.people.empty}
            </span>
          ) : (
            answer.employees.map((employee) => (
              <EmployeeChip key={employee._id} employee={employee} />
            ))
          )}
        </div>
      </QuestionShell>
    );
  }

  return (
    <QuestionShell
      kind="core"
      stepNumber={4}
      heading={describeStrings.people.heading}
      subheading={describeStrings.people.subheading}
      status={status}
      isLast={isLast}
    >
      <PeopleEditor initial={answer} onSubmit={onSubmit} />
    </QuestionShell>
  );
}

interface PeopleEditorProps {
  initial: PeopleAnswer | null;
  onSubmit: (answer: PeopleAnswer) => void;
}

function PeopleEditor({ initial, onSubmit }: PeopleEditorProps) {
  const { data: employees = [] } = useEmployees({ isActive: true });
  const [selected, setSelected] = useState<Employee[]>(
    initial?.employees ?? []
  );
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return [];
    const selectedIds = new Set(selected.map((employee) => employee._id));
    return employees
      .filter((employee) => !selectedIds.has(employee._id))
      .filter((employee) => fullName(employee).toLowerCase().includes(trimmed))
      .slice(0, 6);
  }, [employees, query, selected]);

  const addEmployee = (employee: Employee) => {
    setSelected((prev) => [...prev, employee]);
    setQuery('');
    inputRef.current?.focus();
  };

  const removeEmployee = (employeeId: string) => {
    setSelected((prev) =>
      prev.filter((employee) => employee._id !== employeeId)
    );
  };

  const submit = () => {
    if (selected.length === 0) return;
    onSubmit({ employees: selected });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      submit();
      return;
    }
    if (event.key === 'Backspace' && !query && selected.length > 0) {
      event.preventDefault();
      setSelected((prev) => prev.slice(0, -1));
    }
  };

  return (
    <div className="space-y-4">
      <div className="relative">
        <div
          className={cn(
            'min-h-[48px] bg-white border-2 border-primary/40 rounded-xl p-1.5',
            'flex flex-wrap items-center gap-1.5 shadow-sm'
          )}
          onClick={() => inputRef.current?.focus()}
        >
          {selected.map((employee) => (
            <button
              key={employee._id}
              type="button"
              onClick={() => removeEmployee(employee._id)}
              className="bg-slate-100 border border-slate-200 text-slate-800 text-sm font-medium ps-1 pe-2 py-1 rounded-md inline-flex items-center gap-1.5 hover:bg-slate-200 transition-colors"
            >
              <Avatar employee={employee} size="sm" />
              {fullName(employee)}
              <X className="h-3 w-3 text-slate-400" aria-hidden />
            </button>
          ))}
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              selected.length === 0
                ? describeStrings.people.searchPlaceholder
                : ''
            }
            className="flex-1 bg-transparent min-w-[120px] text-sm text-slate-900 focus:outline-none placeholder-slate-400 px-2 py-1"
            autoFocus
          />
        </div>

        {filtered.length > 0 && (
          <div className="absolute top-full start-0 end-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 overflow-hidden">
            {filtered.map((employee) => (
              <button
                key={employee._id}
                type="button"
                onClick={() => addEmployee(employee)}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-800 hover:bg-slate-50 text-start"
              >
                <Avatar employee={employee} size="md" />
                {fullName(employee)}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-400">
          {describeStrings.people.submitHint}
        </p>
        <Button type="button" onClick={submit} disabled={selected.length === 0}>
          {describeStrings.common.continue}
        </Button>
      </div>
    </div>
  );
}

interface EmployeeChipProps {
  employee: Employee;
}

function EmployeeChip({ employee }: EmployeeChipProps) {
  return (
    <span className="bg-slate-100 border border-slate-200 text-slate-800 text-sm font-medium ps-1 pe-2 py-1 rounded-md inline-flex items-center gap-1.5">
      <Avatar employee={employee} size="sm" />
      {fullName(employee)}
    </span>
  );
}

interface AvatarProps {
  employee: Employee;
  size: 'sm' | 'md';
}

function Avatar({ employee, size }: AvatarProps) {
  const dimensions = size === 'sm' ? 'w-5 h-5 text-[10px]' : 'w-7 h-7 text-xs';
  const { firstName, lastName } = employee.personalInfo;
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

  if (employee.personalInfo.imageUrl) {
    return (
      <img
        src={employee.personalInfo.imageUrl}
        alt={fullName(employee)}
        className={cn('rounded-sm object-cover', dimensions)}
      />
    );
  }

  return (
    <span
      className={cn(
        'rounded-sm bg-slate-200 text-slate-600 font-semibold inline-flex items-center justify-center',
        dimensions
      )}
    >
      {initials}
    </span>
  );
}

function fullName(employee: Employee): string {
  return `${employee.personalInfo.firstName} ${employee.personalInfo.lastName}`;
}
